import { SEGMENT_COLORS, SEGMENT_ORDER } from './constants';
import type { PaycheckResult, PaycheckSegmentKey } from './types';

const VIEW_SIZE = 100;

const CENTER = VIEW_SIZE / 2;

const STROKE_WIDTH = 16;

/** Inset by half the stroke, so the ring's outer edge meets the viewBox. */
const RADIUS = (VIEW_SIZE - STROKE_WIDTH) / 2;

export const RING_CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const RING = {
  cx: CENTER,
  cy: CENTER,
  r: RADIUS,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: STROKE_WIDTH,
} as const;

/** The divider grey; the lighter greys vanish against the result card's fill. */
const EMPTY_RING_COLOR = 'text-accent-grey-400';

interface Arc {
  key: PaycheckSegmentKey;
  length: number;
  /** Clockwise from 12 o'clock. */
  startDegrees: number;
}

const ringArcs = (result: PaycheckResult): Arc[] => {
  let startShare = 0;

  return SEGMENT_ORDER.filter(key => result[key] > 0).map(key => {
    const share = result[key] / result.gross;
    const arc = { key, length: share * RING_CIRCUMFERENCE, startDegrees: startShare * 360 };

    startShare += share;

    return arc;
  });
};

interface PaycheckDonutGraphProps {
  result: PaycheckResult;
}

export const PaycheckDonutGraph = ({ result }: PaycheckDonutGraphProps) => {
  const arcs = ringArcs(result);

  return (
    <svg aria-hidden viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`} className="mx-auto size-48">
      {/* A circle's stroke starts at 3 o'clock; turned back a quarter so take-home leads from the top. */}
      <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
        {/* Pennies of pay can round every line to $0.00, which leaves no segment to draw. */}
        {arcs.length === 0 && <circle {...RING} className={EMPTY_RING_COLOR} />}
        {/*
          Rotated rather than dash-offset, with a full-circumference gap and a solid lone arc, so no dash
          ends on the circle's closing point: some engines draw a notch there.
        */}
        {arcs.map(({ key, length, startDegrees }) => (
          <circle
            key={key}
            {...RING}
            strokeDasharray={length < RING_CIRCUMFERENCE ? `${length} ${RING_CIRCUMFERENCE}` : undefined}
            transform={`rotate(${startDegrees} ${CENTER} ${CENTER})`}
            className={SEGMENT_COLORS[key]}
          />
        ))}
      </g>
    </svg>
  );
};
